/* ============================================================
   js/cache.js  —  ChaosStudio Client-Side Cache Layer
   ============================================================
   ARCHITECTURE LAYER: Application Layer (caching sub-layer)

   PURPOSE:
   - Reduces redundant Firestore reads on read-heavy pages
   - Battle galleries and vote counts served from cache first
   - Cache invalidated on a per-resource TTL basis
   - Graceful stale-while-revalidate pattern for snappy UX
   - Zero dependencies — uses localStorage + in-memory Map

   USAGE:
     import { DataCache } from './cache.js';
     
     // Set data
     DataCache.set('battles_week_2026-W11', entries, 30_000);
     
     // Get data (returns null if expired/missing)
     const entries = DataCache.get('battles_week_2026-W11');
     
     // Fetch-or-cache pattern
     const data = await DataCache.getOrFetch(
       'leaderboard',
       () => fetchLeaderboardFromFirestore(),
       60_000
     );

   CACHE LAYERS:
   1. Memory cache (fastest — lost on page refresh)
   2. localStorage cache (persists across tabs/refreshes)
   ============================================================ */

import { CACHE_CONFIG } from './config.js';

/* ──────────────────────────────────────────────────────────
   CACHE ENTRY SHAPE
   { data, expiresAt, fetchedAt, stale: false }
   ────────────────────────────────────────────────────────── */

class _DataCache {
  constructor() {
    // In-memory store: key → { data, expiresAt, fetchedAt }
    this._mem     = new Map();
    // Track in-flight fetches to avoid thundering-herd
    this._pending = new Map();
    // Stats for debugging
    this._stats   = { hits: 0, misses: 0, stale: 0, fetches: 0 };
  }

  /* ────────────────────────────────────────────────────────
     CORE READ / WRITE
     ──────────────────────────────────────────────────────── */

  /**
   * set(key, data, ttlMs)
   * Stores data in both memory and localStorage.
   */
  set(key, data, ttlMs = CACHE_CONFIG.BATTLES_TTL_MS) {
    const entry = {
      data,
      fetchedAt:  Date.now(),
      expiresAt:  Date.now() + ttlMs,
    };
    this._mem.set(key, entry);
    this._lsSet(key, entry);
  }

  /**
   * get(key)
   * Returns cached data if fresh, or null if expired/missing.
   */
  get(key) {
    // 1. Check memory first (fastest)
    const memEntry = this._mem.get(key);
    if (memEntry) {
      if (Date.now() < memEntry.expiresAt) {
        this._stats.hits++;
        return memEntry.data;
      }
      this._mem.delete(key);
    }

    // 2. Check localStorage (survives page refresh)
    const lsEntry = this._lsGet(key);
    if (lsEntry) {
      if (Date.now() < lsEntry.expiresAt) {
        // Re-hydrate memory cache
        this._mem.set(key, lsEntry);
        this._stats.hits++;
        return lsEntry.data;
      }
      this._lsDelete(key);
    }

    this._stats.misses++;
    return null;
  }

  /**
   * getStale(key)
   * Returns data even if expired — useful for stale-while-revalidate.
   * Returns null only if key never existed.
   */
  getStale(key) {
    const memEntry = this._mem.get(key);
    if (memEntry) { this._stats.stale++; return memEntry.data; }
    const lsEntry = this._lsGet(key);
    if (lsEntry) { this._stats.stale++; return lsEntry.data; }
    return null;
  }

  /**
   * invalidate(key)
   * Removes a specific cache entry from both stores.
   */
  invalidate(key) {
    this._mem.delete(key);
    this._lsDelete(key);
  }

  /**
   * invalidatePrefix(prefix)
   * Removes all keys starting with the given prefix.
   * Useful for "bust all battle caches" on new submission.
   */
  invalidatePrefix(prefix) {
    for (const key of this._mem.keys()) {
      if (key.startsWith(prefix)) this._mem.delete(key);
    }
    this._lsInvalidatePrefix(prefix);
  }

  /**
   * clear()
   * Clears all cache entries (memory + localStorage).
   */
  clear() {
    this._mem.clear();
    this._lsClearAll();
  }

  /* ────────────────────────────────────────────────────────
     FETCH-OR-CACHE (main pattern)
     ──────────────────────────────────────────────────────── */

  /**
   * getOrFetch(key, fetchFn, ttlMs, options)
   * The main caching pattern. Checks cache first; if miss, calls
   * fetchFn(), caches the result, then returns it.
   *
   * options.staleWhileRevalidate:
   *   If true and data is stale (expired), returns stale data immediately
   *   AND kicks off a background refresh. Great for non-critical data.
   *
   * options.forceRefresh:
   *   If true, bypasses cache and always fetches fresh.
   */
  async getOrFetch(key, fetchFn, ttlMs = CACHE_CONFIG.BATTLES_TTL_MS, options = {}) {
    // Force refresh bypasses cache
    if (!options.forceRefresh) {
      const cached = this.get(key);
      if (cached !== null) return cached;

      // Stale-while-revalidate: return stale data + refresh in background
      if (options.staleWhileRevalidate) {
        const stale = this.getStale(key);
        if (stale !== null) {
          this._backgroundRefresh(key, fetchFn, ttlMs);
          return stale;
        }
      }
    }

    // Deduplicate concurrent fetches for the same key (thundering herd)
    if (this._pending.has(key)) {
      return this._pending.get(key);
    }

    this._stats.fetches++;
    const fetchPromise = fetchFn()
      .then(data => {
        this.set(key, data, ttlMs);
        this._pending.delete(key);
        return data;
      })
      .catch(err => {
        this._pending.delete(key);
        // On fetch error, return stale data if available
        const stale = this.getStale(key);
        if (stale !== null) {
          console.warn(`[Cache] Fetch failed for ${key}, serving stale:`, err.message);
          return stale;
        }
        throw err;
      });

    this._pending.set(key, fetchPromise);
    return fetchPromise;
  }

  /**
   * _backgroundRefresh(key, fetchFn, ttlMs)
   * Silently refreshes a cache entry in the background.
   */
  _backgroundRefresh(key, fetchFn, ttlMs) {
    if (this._pending.has(key)) return; // already refreshing
    const p = fetchFn()
      .then(data => { this.set(key, data, ttlMs); })
      .catch(err  => console.warn(`[Cache] Background refresh failed for ${key}:`, err.message))
      .finally(() => this._pending.delete(key));
    this._pending.set(key, p);
  }

  /* ────────────────────────────────────────────────────────
     VOTE COUNT HELPERS
     Specialised helpers for vote counts (short TTL, incremental)
     ──────────────────────────────────────────────────────── */

  /**
   * getVoteCount(entryId)
   * Returns cached vote count for a design entry.
   */
  getVoteCount(entryId) {
    return this.get(`vote_${entryId}`);
  }

  /**
   * setVoteCount(entryId, count)
   * Caches vote count with the short VOTE_COUNTS_TTL_MS TTL.
   */
  setVoteCount(entryId, count) {
    this.set(`vote_${entryId}`, count, CACHE_CONFIG.VOTE_COUNTS_TTL_MS);
  }

  /**
   * optimisticVoteIncrement(entryId)
   * Immediately increments cached vote count for optimistic UI.
   * The real count from Firestore will overwrite on next refresh.
   */
  optimisticVoteIncrement(entryId) {
    const key     = `vote_${entryId}`;
    const current = this.get(key);
    if (current !== null) {
      this.set(key, current + 1, CACHE_CONFIG.VOTE_COUNTS_TTL_MS);
      return current + 1;
    }
    return null;
  }

  /* ────────────────────────────────────────────────────────
     BATTLE GALLERY HELPERS
     ──────────────────────────────────────────────────────── */

  /**
   * getBattleEntries(weekId)
   */
  getBattleEntries(weekId) {
    return this.get(`battle_entries_${weekId}`);
  }

  /**
   * setBattleEntries(weekId, entries)
   */
  setBattleEntries(weekId, entries) {
    this.set(`battle_entries_${weekId}`, entries, CACHE_CONFIG.BATTLES_TTL_MS);
  }

  /**
   * getLeaderboard()
   */
  getLeaderboard() {
    return this.get('leaderboard_global');
  }

  /**
   * setLeaderboard(data)
   */
  setLeaderboard(data) {
    this.set('leaderboard_global', data, CACHE_CONFIG.LEADERBOARD_TTL_MS);
  }

  /* ────────────────────────────────────────────────────────
     FEATURED DROPS HELPERS  (homepage)
     ──────────────────────────────────────────────────────── */

  getFeaturedDrops() {
    return this.get('featured_drops');
  }

  setFeaturedDrops(data) {
    this.set('featured_drops', data, CACHE_CONFIG.FEATURED_DROPS_TTL_MS);
  }

  /* ────────────────────────────────────────────────────────
     DEBUG STATS
     ──────────────────────────────────────────────────────── */
  getStats() {
    const total = this._stats.hits + this._stats.misses;
    return {
      ...this._stats,
      hitRate: total > 0 ? ((this._stats.hits / total) * 100).toFixed(1) + '%' : '—',
      entries: this._mem.size,
    };
  }

  logStats() {
    console.table(this.getStats());
  }

  /* ────────────────────────────────────────────────────────
     LOCALSTORAGE HELPERS (private)
     ──────────────────────────────────────────────────────── */
  _lsSet(key, entry) {
    try {
      localStorage.setItem(
        CACHE_CONFIG.KEY_PREFIX + key,
        JSON.stringify(entry)
      );
    } catch(e) {
      // localStorage full or unavailable — silently fail, memory cache still works
      if (e.name === 'QuotaExceededError') this._lsEvictOldest();
    }
  }

  _lsGet(key) {
    try {
      const raw = localStorage.getItem(CACHE_CONFIG.KEY_PREFIX + key);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }

  _lsDelete(key) {
    try { localStorage.removeItem(CACHE_CONFIG.KEY_PREFIX + key); } catch {}
  }

  _lsInvalidatePrefix(prefix) {
    try {
      const fullPrefix = CACHE_CONFIG.KEY_PREFIX + prefix;
      const keys = Object.keys(localStorage).filter(k => k.startsWith(fullPrefix));
      keys.forEach(k => localStorage.removeItem(k));
    } catch {}
  }

  _lsClearAll() {
    try {
      const keys = Object.keys(localStorage).filter(k =>
        k.startsWith(CACHE_CONFIG.KEY_PREFIX));
      keys.forEach(k => localStorage.removeItem(k));
    } catch {}
  }

  /**
   * Evict oldest localStorage entries when storage is full.
   */
  _lsEvictOldest() {
    try {
      const prefix  = CACHE_CONFIG.KEY_PREFIX;
      const entries = Object.keys(localStorage)
        .filter(k => k.startsWith(prefix))
        .map(k => {
          try {
            const v = JSON.parse(localStorage.getItem(k));
            return { key: k, fetchedAt: v.fetchedAt || 0 };
          } catch { return { key: k, fetchedAt: 0 }; }
        })
        .sort((a, b) => a.fetchedAt - b.fetchedAt);

      // Remove oldest 20%
      const toRemove = Math.max(1, Math.floor(entries.length * 0.2));
      entries.slice(0, toRemove).forEach(e => localStorage.removeItem(e.key));
    } catch {}
  }
}

/* ──────────────────────────────────────────────────────────
   PAGE-LEVEL CACHE MANAGER
   Higher-level wrapper that ties pages to cache keys + TTLs
   ────────────────────────────────────────────────────────── */
export class PageCache {
  /**
   * battles(weekId, fetchFn)
   * Cache-or-fetch pattern specifically for battle pages.
   */
  static battles(weekId, fetchFn) {
    return DataCache.getOrFetch(
      `battle_entries_${weekId}`,
      fetchFn,
      CACHE_CONFIG.BATTLES_TTL_MS,
      { staleWhileRevalidate: true }
    );
  }

  /**
   * leaderboard(fetchFn)
   */
  static leaderboard(fetchFn) {
    return DataCache.getOrFetch(
      'leaderboard_global',
      fetchFn,
      CACHE_CONFIG.LEADERBOARD_TTL_MS,
      { staleWhileRevalidate: true }
    );
  }

  /**
   * voteCounts(batchIds, fetchFn)
   * Fetches vote counts for multiple entry IDs at once.
   * Only fetches IDs that are not already cached.
   */
  static async voteCounts(batchIds, fetchFn) {
    const uncached = batchIds.filter(id => DataCache.getVoteCount(id) === null);
    if (uncached.length > 0) {
      const fresh = await fetchFn(uncached);
      Object.entries(fresh).forEach(([id, count]) => DataCache.setVoteCount(id, count));
    }
    // Return full map from cache
    return Object.fromEntries(
      batchIds.map(id => [id, DataCache.getVoteCount(id) ?? 0])
    );
  }

  /**
   * featuredDrops(fetchFn)
   * Long TTL — homepage featured products rarely change.
   */
  static featuredDrops(fetchFn) {
    return DataCache.getOrFetch(
      'featured_drops',
      fetchFn,
      CACHE_CONFIG.FEATURED_DROPS_TTL_MS,
      { staleWhileRevalidate: true }
    );
  }
}

/* ──────────────────────────────────────────────────────────
   SINGLETON EXPORT
   ────────────────────────────────────────────────────────── */
export const DataCache = new _DataCache();

export default DataCache;
